/**
 * PlanningGantt — resource × day overview across the selected range. Rows are
 * resources, columns are days; each (resource, day) cell shows that day's
 * items as chips. Click a chip to edit. Good for spotting load over a
 * week/month at a glance.
 */
import { useMemo } from 'react';
import {
  type PlannerResource,
  type PlannerService,
  type PlannerScheduledItem,
  type PlannerTaxonomies,
} from '@/services/api';
import { hm, addDays, addMonths, ymd, ym, stateInfo, STATE_COLORS } from './plannerShared';

export default function PlanningGantt({ from, to, granularity = 'day', items, resources, services, tax, uncoveredItemIds, onEdit }: {
  from: Date;
  to: Date;
  granularity?: 'day' | 'month';
  items: PlannerScheduledItem[];
  resources: PlannerResource[];
  services: PlannerService[];
  tax: PlannerTaxonomies | null;
  uncoveredItemIds?: Set<string>;
  onEdit: (item: PlannerScheduledItem) => void;
}) {
  const activeResources = useMemo(() => resources.filter((r) => r.active), [resources]);

  // Buckets: day columns (week/month range) or month columns (year range).
  const buckets = useMemo(() => {
    const out: { key: string; top: string; sub: string; weekend: boolean }[] = [];
    if (granularity === 'month') {
      for (let d = new Date(from.getFullYear(), from.getMonth(), 1); d < to && out.length < 24; d = addMonths(d, 1)) {
        out.push({ key: ym(d), top: d.toLocaleDateString('es-ES', { month: 'short' }), sub: String(d.getFullYear()), weekend: false });
      }
    } else {
      for (let d = new Date(from); d < to && out.length < 62; d = addDays(d, 1)) {
        out.push({
          key: ymd(d),
          top: d.toLocaleDateString('es-ES', { weekday: 'short' }),
          sub: d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
          weekend: d.getDay() === 0 || d.getDay() === 6,
        });
      }
    }
    return out;
  }, [from, to, granularity]);

  const keyOf = (d: Date) => (granularity === 'month' ? ym(d) : ymd(d));
  const svcName = (id?: string | null) => services.find((s) => s._id === id)?.name || 'Servicio';

  const byCell = useMemo(() => {
    const m = new Map<string, PlannerScheduledItem[]>();
    for (const it of items) {
      const key = `${it.resourceId}|${keyOf(new Date(it.start))}`;
      (m.get(key) || m.set(key, []).get(key)!).push(it);
    }
    for (const list of m.values()) list.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, granularity]);

  if (activeResources.length === 0) {
    return <p className="text-sm text-gray-500">No hay recursos activos. Crea recursos en la pestaña Recursos.</p>;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
      <div className="min-w-fit">
        {/* Header row */}
        <div className="flex border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="shrink-0 w-40 px-3 py-2 text-xs font-semibold text-gray-500 border-r border-gray-200">Recurso</div>
          {buckets.map((b) => (
            <div key={b.key} className={`shrink-0 w-28 px-2 py-2 text-center border-r border-gray-100 ${b.weekend ? 'bg-gray-50' : ''}`}>
              <div className="text-[11px] font-medium text-gray-700 capitalize">{b.top}</div>
              <div className="text-[11px] text-gray-400">{b.sub}</div>
            </div>
          ))}
        </div>
        {/* Resource rows */}
        {activeResources.map((r) => (
          <div key={r._id} className="flex border-b border-gray-100 last:border-b-0">
            <div className="shrink-0 w-40 px-3 py-2 text-xs font-medium text-gray-700 border-r border-gray-200 truncate" title={r.name}>{r.name}</div>
            {buckets.map((b) => {
              const cell = byCell.get(`${r._id}|${b.key}`) || [];
              return (
                <div key={b.key} className={`shrink-0 w-28 p-1 border-r border-gray-100 space-y-1 align-top ${b.weekend ? 'bg-gray-50/50' : ''}`}>
                  {cell.map((it) => {
                    const st = stateInfo(tax, it.stateNodeId);
                    const uncovered = uncoveredItemIds?.has(it._id) || false;
                    const color = uncovered ? 'bg-amber-500' : (st && STATE_COLORS[st.id]) || 'bg-vida-600';
                    return (
                      <button key={it._id} onClick={() => onEdit(it)} className={`block w-full ${color} text-white rounded px-1 py-0.5 text-[10px] text-left truncate hover:opacity-90`} title={uncovered ? 'Sin presencia asignada para este horario' : `${hm(new Date(it.start))} ${svcName(it.serviceId)}`}>
                        {uncovered ? '⚠ ' : ''}{hm(new Date(it.start))} {svcName(it.serviceId)}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
